// src/components/friendship/FriendshipVibeHero.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Users, ArrowRight, Sparkles, Star, Zap, Heart, Shield } from 'lucide-react';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';
import { usePrivacyAuth } from '../../context/PrivacyAuthContext';

const FriendshipVibeHero = () => {
  const { setCurrentView } = useApp();
  const { user } = usePrivacyAuth();

  return (
    <section className="bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 pt-20 sm:pt-32 pb-12 sm:pb-16 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-coral-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gold-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-5xl mx-auto space-y-8"
        >
          {/* Floating Elements */}
          <div className="relative">
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-4 -left-4 md:-top-8 md:-left-8 text-3xl md:text-5xl"
            >
              👥
            </motion.div>
            <motion.div
              animate={{ y: [10, -10, 10] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-2 -right-6 md:-top-4 md:-right-12 text-3xl md:text-5xl"
            >
              💜
            </motion.div>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity }}
              className="absolute top-6 left-6 md:top-12 md:left-12 text-xl md:text-2xl"
            >
              ✨
            </motion.div>
          </div>

          {/* Main Hero Content */}
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="w-6 h-6 text-red-400" />
              <span className="px-3 py-1 bg-gradient-to-r from-teal-500/20 to-coral-500/20 border border-teal-500/30 rounded-full text-teal-300 font-medium">
                Friendship Vibe
              </span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              Transform Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-coral-400 to-gold-400 animate-gradient-shift">
                Friendship Game
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Want to get better at friendships? Understand your friendship style, recognise patterns holding you back, and learn the skills that make great friendships even better.
              <span className="text-teal-300 font-semibold"> Real skills for deeper friendships.</span>
            </p>
          </motion.div>

          {/* Key Value Props */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex flex-wrap justify-center gap-4 text-sm"
          >
            {[
              { icon: Users, text: 'Understand yourself AND your squad', color: 'text-teal-400' },
              { icon: Star, text: 'Science-backed insights', color: 'text-teal-500' },
              { icon: Shield, text: 'Your data stays private', color: 'text-green-400' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10"
              >
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-white font-medium">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex justify-center"
          >
            <Button
              size="lg"
              className="min-w-[220px] bg-gradient-to-r from-teal-600 to-coral-600 hover:from-teal-700 hover:to-coral-700 shadow-xl text-lg font-semibold"
              onClick={() => setCurrentView('vibe-assessment-intro')}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Friendship Vibe Check
            </Button>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="max-w-2xl mx-auto mt-12"
          >
            <div className="relative group">
              <motion.img
                src="/images/friendship-00-hero.png"
                alt="Friends connecting and building meaningful relationships"
                className="w-full h-auto rounded-2xl shadow-2xl group-hover:shadow-3xl transition-shadow duration-300"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-slate-900/20 to-transparent pointer-events-none"></div>
            </div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="text-center"
          >
            <p className="text-slate-400 text-sm mb-4">
              Start instantly • No email needed • Delete your data any time from Privacy &amp; Data
            </p>
            <div className="flex justify-center items-center gap-8 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                Research-backed
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                10-15 min sessions
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                Privacy-first
              </span>
            </div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="pt-4"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-slate-400"
            >
              <div className="w-5 h-8 border-2 border-slate-600 rounded-full mx-auto relative">
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-1 h-2 bg-teal-400 rounded-full absolute left-1/2 top-1 transform -translate-x-1/2"
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FriendshipVibeHero;